import { useMemo } from 'react';

import { NodeStatus, ResourceTypeExplorer, TestStatus } from '@dbt-labs/dbt-dag';
import {
  Icon,
  Ryecon,
  RyeconHelp,
  RyeconStatusError,
  RyeconStatusSuccess,
  RyeconStatusWarning,
  Tooltip,
} from '@dbt-labs/sourdough';

import { TestAppliedStateNestedNode } from '@/__generated__/graphql';
import { truthy } from '@/util/array';
import { resourceTypes } from '@/util/resourceType';
import { CollapsibleSection } from '../CollapsibleSection';
import { ResourceStatusTable, ResourceWithStatus } from '../ResourceStatusTable';

enum TestResultStatus {
  unknown = 0,
  pass = 1,
  warn = 2,
  error = 3,
}

const testResultStatusRyeconMap: Record<number, Ryecon> = {
  0: RyeconHelp,
  1: RyeconStatusSuccess,
  2: RyeconStatusWarning,
  3: RyeconStatusError,
};

// used to sort test results by severity
export const testStatusOrder: Record<TestStatus, number> = {
  error: 0,
  fail: 1,
  warn: 2,
  pass: 3,
  skipped: 4,
  reused: 5,
};

type TestResultsSectionParams = {
  resourceType: ResourceTypeExplorer | 'unknown';
  tests: TestAppliedStateNestedNode[] | undefined;
};

export const TestResultsSection = ({
  resourceType,
  tests,
}: TestResultsSectionParams) => {
  const { testResources, testResultStatus, tooltipMessage } = useMemo(() => {
    if (!tests) {
      return { testResources: undefined, testResultStatus: TestResultStatus.unknown };
    }

    let warnCount: number = 0;
    let errorCount: number = 0;

    const testResources: ResourceWithStatus[] = tests?.filter(truthy).map((test) => {
      const testStatus = test.executionInfo.lastRunStatus;
      if (testStatus === 'warn') {
        warnCount++;
      } else if (testStatus === 'error' || testStatus === 'fail') {
        errorCount++;
      }

      return {
        name: test.name || '',
        uniqueId: test.uniqueId,
        resourceType: resourceTypes[8],
        status: test.executionInfo.lastRunStatus as NodeStatus,
      };
    });

    let testResultStatus = TestResultStatus.unknown;
    let tooltipMessage = `The status of this ${resourceType}'s tests could not be determined`;
    if (testResources.length > 0 && warnCount === 0 && errorCount === 0) {
      testResultStatus = TestResultStatus.pass;
      tooltipMessage = `This ${resourceType}'s tests are all passing`;
    } else if (errorCount > 0) {
      testResultStatus = TestResultStatus.error;
      tooltipMessage = `One or more tests of this ${resourceType} are failing`;
    } else if (testResources.length === 0) {
      testResultStatus = TestResultStatus.warn;
      tooltipMessage = `This ${resourceType} has no tests configured`;
    } else if (warnCount > 0) {
      testResultStatus = TestResultStatus.warn;
      tooltipMessage = `One or more tests of this ${resourceType} are warning`;
    }

    // sort by test status severity, then name alphabetically if tie
    testResources.sort((a, b) => {
      const statusComparison =
        testStatusOrder[a.status as TestStatus] -
        testStatusOrder[b.status as TestStatus];
      if (statusComparison !== 0) {
        return statusComparison;
      }
      return a.name.localeCompare(b.name);
    });

    return { testResources, testResultStatus, tooltipMessage };
  }, [resourceType, tests]);

  return (
    <>
      {testResources && (
        <CollapsibleSection
          closeAltText={`Hide test result details`}
          expandAltText={`Show test result details`}
          toExpand={<ResourceStatusTable resources={testResources} />}
          disable={testResources.length === 0}
          shouldIndent={testResources.length > 0}
          className="-mb-[1px] mt-1 flex w-full overflow-hidden border-b border-borderMuted p-4 text-fgMain"
        >
          <span className="flex" data-testid={`test-result-status-${testResultStatus}`}>
            <span className="flex items-center">
              <Tooltip
                content={tooltipMessage}
                className="pointer-events-auto flex items-center"
              >
                <Icon
                  size="md"
                  ryecon={testResultStatusRyeconMap[testResultStatus]}
                  className="pointer-events-auto align-middle"
                />
                <div className="sr-only">{tooltipMessage}</div>
              </Tooltip>
            </span>
            <span>
              <p className="font-label-lg mx-2 align-middle">Test results</p>
            </span>
          </span>
        </CollapsibleSection>
      )}
    </>
  );
};
